'use client';

import { Box, Flex, Link, Typography } from '@coolblue-development/freddy';
import clickReviewsTrigger from '@coolblue-development/ga4-events/triggers/ComposedProductContent/clickReviewsTrigger';
import selectItemInComposedTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/selectItemInComposedTrigger';
import viewElementTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/viewElementTrigger';
import { TrackWhenVisible, useAnalytics } from '@coolblue-development/next-analytics/client';
import { ProductTitle, SquareProductImage } from '@coolblue-development/next-patterns/client';
import { PropertyGroup, SubProducts } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import FullSpecificationsLink from './FullSpecificationLink';
import SpecificationsModal from './SpecificationsModal';

type Props = {
  productId: number;
  subProducts: SubProducts[];
  productTypeId: number;
  propertyGroups: PropertyGroup[];
};

export default function ComposedProductSpecifications({
  productId,
  subProducts,
  productTypeId,
  propertyGroups
}: Props) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('webshop.productPage');
  const { isMobile } = useBreakpoints();
  const { trackEvent } = useAnalytics();

  function onImageClick(index: number, productId: number) {
    const params = {
      index: index + 1,
      total_indices: subProducts.length,
      method: 'image' as const,
      item_id: productId
    };
    trackEvent(selectItemInComposedTrigger(params));
  }

  function onTitleClick(index: number, productId: number) {
    const params = {
      index: index + 1,
      total_indices: subProducts.length,
      method: 'title' as const,
      item_id: productId
    };
    trackEvent(selectItemInComposedTrigger(params));
  }

  function onReviewsClick(productId: number) {
    trackEvent(
      clickReviewsTrigger({
        item_id: productId
      })
    );
  }

  return (
    <TrackWhenVisible
      trigger={viewElementTrigger({
        sub_feature: 'composed_content'
      })}
    >
      <Box mt={{ xs: 400, lg: 0 }}>
        <Typography variant='h3'>{t('composedProduct.includedInPackage')}</Typography>
        <Typography>{t('composedProduct.includedInPackageDescription')}</Typography>
        {subProducts.map(
          ({ product, quantity }, index) =>
            product?.urls?.product &&
            product.urls.productReviews && (
              <Flex alignItems='flex-start' gap={400} mb={400} key={index}>
                <Link href={product?.urls?.product} onClick={() => onImageClick(index, product.id)}>
                  <SquareProductImage
                    alt={product.name}
                    imageId={product.primaryImage.id}
                    size={isMobile ? 'medium' : 'large'}
                    loading='lazy'
                  />
                </Link>
                <ProductTitle>
                  <ProductTitle.Title>
                    <Link
                      href={product.urls.product}
                      onClick={() => onTitleClick(index, product.id)}
                    >
                      {quantity > 1 ? `${quantity}x ${product.name}` : product.name}
                    </Link>
                  </ProductTitle.Title>
                  <Link
                    href={product.urls.productReviews}
                    onClick={() => onReviewsClick(product.id)}
                  >
                    <ProductTitle.Review
                      value={product.reviewMetaData?.averageRating ?? 0}
                      reviewCount={product.reviewMetaData?.count ?? 0}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Link>
                </ProductTitle>
              </Flex>
            )
        )}

        <FullSpecificationsLink onMobileClick={() => setOpen(true)} isComposed>
          {t('moreSpecifications')}
        </FullSpecificationsLink>

        {isMobile && (
          <SpecificationsModal
            productId={productId}
            open={open}
            onClose={() => setOpen(false)}
            propertyGroups={propertyGroups}
            productTypeId={productTypeId}
            subProducts={subProducts}
          />
        )}
      </Box>
    </TrackWhenVisible>
  );
}
